import {useHelpTourUiStore} from "../store/help-tour-ui";

import {takeSheetScrollSnapshot} from "./help-tour-scroll";

const SCROLL_CONTAINER_SELECTOR = "[data-scroll-container]";
const DRIVER_ACTIVE_CLASS = "driver-active";
const DRIVER_POPOVER_SELECTOR = ".driver-popover";

type ScrollSnapshot = {
  el: HTMLElement;
  top: number;
};

type HelpTourSessionOptions = {
  /** Full-viewport sheets should not render the primary banner (iOS status-bar tint). */
  showBanner?: boolean;
  /** List tours restore main scroll; sheet tours should not touch the page scroller. */
  restoreScroll?: boolean;
};

let sessionShowBanner = false;
let sessionRestoreScroll = false;
let scrollSnapshot: ScrollSnapshot | null = null;
let driverActiveObserver: MutationObserver | null = null;
let teardownPollId: number | null = null;
let wasDriverActive = false;
let sessionOpen = false;

function getScrollContainer(): HTMLElement | null {
  const el = document.querySelector(SCROLL_CONTAINER_SELECTOR);
  return el instanceof HTMLElement ? el : null;
}

function isDriverUiVisible() {
  return (
    document.body.classList.contains(DRIVER_ACTIVE_CLASS) ||
    document.querySelector(DRIVER_POPOVER_SELECTOR) !== null
  );
}

function scrubDriverDom() {
  document.body.classList.remove(DRIVER_ACTIVE_CLASS, "driver-fade", "driver-simple");
  document.querySelectorAll(DRIVER_POPOVER_SELECTOR).forEach((node) => {
    node.remove();
  });
  document.querySelectorAll(".driver-overlay").forEach((node) => {
    node.remove();
  });
  document.getElementById("driver-dummy-element")?.remove();
}

function stopDriverActiveObserver() {
  driverActiveObserver?.disconnect();
  driverActiveObserver = null;
  wasDriverActive = false;
}

function stopTeardownPoll() {
  if (teardownPollId !== null) {
    window.clearInterval(teardownPollId);
    teardownPollId = null;
  }
}

function maybeEndSessionFromDriverState() {
  if (!sessionOpen) {
    return;
  }

  const driverVisible = isDriverUiVisible();

  if (driverVisible) {
    wasDriverActive = true;
    return;
  }

  if (wasDriverActive) {
    endHelpTourSession();
  }
}

function watchDriverTeardown() {
  stopDriverActiveObserver();

  driverActiveObserver = new MutationObserver(() => {
    maybeEndSessionFromDriverState();
  });

  driverActiveObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
    childList: true,
    subtree: true,
  });
}

function startTeardownPoll() {
  stopTeardownPoll();
  teardownPollId = window.setInterval(maybeEndSessionFromDriverState, 200);
}

export function beginHelpTourSession(
  stepIndex: number,
  total: number,
  options: HelpTourSessionOptions = {},
) {
  endHelpTourSession();

  sessionShowBanner = options.showBanner === true;
  sessionRestoreScroll = options.restoreScroll !== false;
  sessionOpen = true;

  if (sessionShowBanner) {
    useHelpTourUiStore.getState().startTour(stepIndex, total);
  }

  if (sessionRestoreScroll) {
    const container = getScrollContainer();
    if (container) {
      scrollSnapshot = {el: container, top: container.scrollTop};
    }
  }

  watchDriverTeardown();
  startTeardownPoll();
}

export function updateHelpTourChrome(activeIndex: number, total: number) {
  if (!sessionShowBanner) {
    return;
  }
  useHelpTourUiStore.getState().setProgress(activeIndex, total);
}

export function endHelpTourSession() {
  sessionOpen = false;
  sessionShowBanner = false;
  sessionRestoreScroll = false;
  stopDriverActiveObserver();
  stopTeardownPoll();

  useHelpTourUiStore.getState().endTour();
  scrubDriverDom();

  if (scrollSnapshot) {
    scrollSnapshot.el.scrollTo({top: scrollSnapshot.top, left: 0, behavior: "auto"});
    scrollSnapshot = null;
  }

  const sheetScrollSnapshot = takeSheetScrollSnapshot();
  if (sheetScrollSnapshot) {
    sheetScrollSnapshot.el.scrollTo({top: sheetScrollSnapshot.top, left: 0, behavior: "auto"});
  }
}
